<template>
	<transition name="slide">
		<b-form class="new-payment-info" @submit.prevent="book">
			<div class="new-payment-info__grid">
				<section class="new-payment-info__section">
					<h3>{{translation.title}}</h3>

					<div class="new-payment-info__brands">
						<label v-if="canUseBankTicket">
							<img src="/static/boleto.gif" alt="Boleto">
							<input type="radio" v-model="paymentMethod" :value="PaymentMethods.BANK_TICKET" required>
						</label>
						<label>
							<img src="/static/visa.jpg" alt="Visa">
							<input type="radio" v-model="paymentMethod" :value="PaymentMethods.VISA" required>
						</label>
						<label>
							<img src="/static/mastercard.jpg" alt="Mastercard">
							<input type="radio" v-model="paymentMethod" :value="PaymentMethods.MASTER" required>
						</label>
						<label>
							<img src="/static/americanExpress.jpg" alt="American Express">
							<input type="radio" v-model="paymentMethod" :value="PaymentMethods.AMEX" required>
						</label>
					</div>

					<template v-if="!isBankTicket">
						<b-form-input
							v-model="sale.creditCard.cardNumber"
							class="new-payment-info__control new-payment-info__control--large"
							type="text"
							:placeholder="translation.numberCreditCard"
							v-mask="'9999 9999 9999 9999'"
							required
						/>

						<b-form-input
							v-model="sale.creditCard.holder"
							class="new-payment-info__control new-payment-info__control--large"
							type="text"
							:placeholder="translation.nameCreditCard"
							required
						/>

						<div class="new-payment-info__compact-row">
							<label>
								<span>{{translation.code}}</span>
								<b-form-input
									v-model="sale.creditCard.securityCode"
									class="new-payment-info__control new-payment-info__control--small"
									type="text"
									v-mask="'9999'"
									required
								/>
							</label>

							<label>
								<span>{{expirationLabel}}</span>
								<div class="new-payment-info__expiration">
									<b-form-select
										v-model="sale.creditCard.expirationDate.month"
										class="new-payment-info__control new-payment-info__control--select"
										:options="months"
										required
									/>
									<b-form-select
										v-model="sale.creditCard.expirationDate.year"
										class="new-payment-info__control new-payment-info__control--select"
										:options="years"
										required
									/>
								</div>
							</label>
						</div>

						<label>
							<span>{{translation.installments}}</span>
							<b-form-select
								v-model="sale.installments"
								class="new-payment-info__control new-payment-info__control--small"
								:options="installmentOptions"
								required
							/>
						</label>
					</template>
				</section>

				<section class="new-payment-info__section">
					<h3>{{translation.receiptData}}</h3>

					<div class="new-payment-info__receipt">
						<b-form-input
							v-model="sale.customer.address.zipCode"
							class="new-payment-info__control"
							type="text"
							:placeholder="translation.zipCode"
							v-mask="country.id === Countries.BRAZIL.id ? '99999-999' : null"
							required
							@blur="fillAddressFieldsOnValidZipCode"
						/>
						<b-form-input
							v-model="sale.customer.address.state"
							class="new-payment-info__control"
							type="text"
							:placeholder="translation.state"
							required
							:disabled="country.id === Countries.BRAZIL.id"
						/>
						<b-form-input
							v-model="sale.customer.address.city"
							class="new-payment-info__control"
							type="text"
							:placeholder="translation.city"
							required
							:disabled="country.id === Countries.BRAZIL.id"
						/>
						<b-form-input
							v-model="sale.customer.address.district"
							class="new-payment-info__control"
							type="text"
							:placeholder="translation.district"
							required
							:disabled="country.id === Countries.BRAZIL.id"
						/>
						<b-form-input
							v-model="sale.customer.address.street"
							class="new-payment-info__control"
							type="text"
							:placeholder="translation.street"
							required
						/>

						<div class="new-payment-info__receipt-row">
							<b-form-input
								v-model="sale.customer.address.number"
								class="new-payment-info__control"
								type="text"
								:placeholder="translation.number"
								required
							/>
							<b-form-input
								v-model="sale.customer.address.complement"
								class="new-payment-info__control"
								type="text"
								:placeholder="translation.complement"
							/>
						</div>

						<div v-if="country.id === Countries.BRAZIL.id" class="new-payment-info__inscription">
							<span>{{translation.inscription}}</span>
							<label
								v-for="inscriptionType in inscriptionTypes"
								:key="inscriptionType.id"
							>
								<input
									v-model="selectedInscriptionType"
									type="radio"
									name="new-payment-inscription-type"
									:value="inscriptionType"
									required
									@change="selectInscriptionType"
								>
								{{inscriptionType.name}}
							</label>
							<b-form-input
								v-model="sale.customer.inscription"
								class="new-payment-info__control"
								type="text"
								v-mask="selectedInscriptionType.mask"
								required
								@blur="validateMask(sale.customer.inscription, selectedInscriptionType.mask, () => sale.customer.inscription = null)"
							/>
						</div>
					</div>
				</section>
			</div>

			<slot></slot>

			<div class="new-payment-info__actions">
				<slot name="secondary-action"></slot>
				<button type="submit" class="new-payment-info__submit">
					{{button}}
				</button>
			</div>
		</b-form>
	</transition>
</template>

<script>
import moment from 'moment';
import mask from 'awesome-mask';
import { translate } from '../../assets/translations';
import Countries from '../../enums/Countries';
import InscriptionType from '../../enums/InscriptionType';
import PaymentMethods from '../../enums/PaymentMethods';
import Sale from '../../model/Sale';
import { viaCep } from '../../util';

export default {
	directives: { mask },
	props: {
		bookingResponse: {
			type: Object,
			default: null,
		},
		country: {
			type: Object,
			required: true,
		},
		event: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			Countries,
			PaymentMethods,
			inscriptionTypes: Object.values(InscriptionType),
			paymentMethod: null,
			sale: this.setSale(),
			selectedInscriptionType: InscriptionType.CPF,
		};
	},
	computed: {
		button() {
			return this.bookingResponse
				? this.translation.book.retry
				: this.translation.book.firstTry;
		},
		canUseBankTicket() {
			return this.event.paidByBoleto && this.country.id === Countries.BRAZIL.id;
		},
		expirationLabel() {
			return String(this.translation.expireDate || '').split('(')[0].trim();
		},
		installmentOptions() {
			const installments = Number(this.event.installments) > 0 ? Number(this.event.installments) : 1;
			return [...Array(installments)].map((value, index) => index + 1);
		},
		isBankTicket() {
			return this.paymentMethod && this.paymentMethod.id === PaymentMethods.BANK_TICKET.id;
		},
		months() {
			return [...Array(12)].map((value, index) => index + 1).map(month => (month < 10 ? `0${month}` : `${month}`));
		},
		translation() {
			return translate(this.$store.state.language).creditCardInfo;
		},
		years() {
			let years = [];
			let year = moment().year();
			for (let index = 0; index <= 10; index++) {
				years.push(year + index);
			}
			return years;
		},
	},
	methods: {
		book() {
			if (!this.paymentMethod) return;

			this.sale.creditCard.brandId = this.paymentMethod.id;

			if (this.isBankTicket) {
				this.$emit('book', this.buildBankTicket());
				return;
			}

			this.$emit('book', this.sale);
		},
		buildBankTicket() {
			return {
				brandId: PaymentMethods.BANK_TICKET.id,
				address: `${this.sale.customer.address.street}, ${this.sale.customer.address.number}${this.sale.customer.address.complement ? `, ${this.sale.customer.address.complement}` : ''}`,
				city: this.sale.customer.address.city,
				state: this.sale.customer.address.state,
				uf: this.sale.customer.address.uf,
				district: this.sale.customer.address.district,
				inscriptionNumber: this.sale.customer.inscription,
				inscriptionTypeId: this.sale.customer.inscriptionTypeId,
				payerName: this.sale.customer.name,
				zipCode: this.sale.customer.address.zipCode,
			};
		},
		async fillAddressFieldsOnValidZipCode() {
			const ZIP_CODE_LENGTH = 9;
			if (
				!this.sale.customer.address.zipCode
				|| this.sale.customer.address.zipCode.length !== ZIP_CODE_LENGTH
				|| this.country.id !== Countries.BRAZIL.id
			) {
				return;
			}

			const address = await viaCep(this.sale.customer.address.zipCode);
			if (address) {
				this.sale.customer.address.city = address.localidade;
				this.sale.customer.address.state = address.estado || address.uf;
				this.sale.customer.address.uf = address.uf;
				this.sale.customer.address.street = address.logradouro;
				this.sale.customer.address.district = address.bairro;
				return;
			}

			this.sale.customer.address.zipCode = null;
			this.sale.customer.address.city = null;
			this.sale.customer.address.state = null;
			this.sale.customer.address.uf = null;
			this.sale.customer.address.street = null;
			this.sale.customer.address.district = null;
		},
		selectInscriptionType() {
			this.sale.customer.inscription = null;
			this.sale.customer.inscriptionTypeId = this.selectedInscriptionType.id;
		},
		setSale() {
			const creditCard = {
				cardNumber: null,
				expirationDate: {
					month: null,
					year: null,
					toJSON() {
						return `${this.month}/${this.year}`;
					},
				},
				securityCode: null,
				holder: null,
				brandId: PaymentMethods.MASTER.id,
			};
			const customer = {
				name: null,
				inscription: null,
				inscriptionTypeId: InscriptionType.CPF.id,
				address: {
					state: null,
					uf: null,
					city: null,
					district: null,
					street: null,
					number: null,
					complement: null,
					zipCode: null,
				},
			};
			return new Sale(creditCard, customer, 1);
		},
		validateMask(element, maskValue, actionAfterValidate) {
			if (!(element && maskValue && element.length === maskValue.length)) {
				actionAfterValidate();
			}
		},
	},
	watch: {
		paymentMethod(newValue) {
			this.$emit('handlePaymentMethod', newValue);
		},
	},
};
</script>

<style scoped lang="scss">
.new-payment-info {
	box-sizing: border-box;
	width: 100%;
	max-width: 1082px;
	margin: 83px 0 0 auto;
	color: #5a5a5a;
}

.new-payment-info__grid {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: 70px;
}

.new-payment-info__section {
	min-width: 0;
}

.new-payment-info__section:first-child {
	flex: 0 1 436px;
}

.new-payment-info__section:nth-child(2) {
	flex: 0 1 521px;
}

.new-payment-info__section h3 {
	margin: 0 0 70px;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-payment-info__section h3::before {
	content: "";
	display: block;
	width: 23px;
	height: 12px;
	margin: 0 0 27px;
	background: url("data:image/svg+xml,%3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 2L11.5 10L21 2' stroke='%2366ACC6' stroke-width='3'/%3E%3C/svg%3E") center / contain no-repeat;
}

.new-payment-info__brands {
	display: flex;
	flex-wrap: wrap;
	gap: 24px 31px;
	margin-bottom: 37px;
}

.new-payment-info__brands label {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 18px;
	margin: 0;
	cursor: pointer;
}

.new-payment-info__brands img {
	display: block;
	width: 57px;
	height: 36px;
	object-fit: contain;
}

.new-payment-info__brands input,
.new-payment-info__inscription input[type="radio"] {
	width: 17px;
	height: 17px;
	margin: 0;
	accent-color: #66acc6;
}

.new-payment-info__control,
.new-payment-info /deep/ .form-control,
.new-payment-info /deep/ .custom-select {
	box-sizing: border-box;
	width: 100%;
	border: 1px solid rgba(0, 0, 0, .2);
	border-radius: 10px;
	background-color: #ffffff;
	color: #5a5a5a;
	font-family: inherit;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
	outline: none;
	box-shadow: none;
	background-image: none;
	-webkit-appearance: menulist;
	-moz-appearance: auto;
	appearance: auto;
}

.new-payment-info__control::placeholder,
.new-payment-info /deep/ .form-control::placeholder {
	color: #5a5a5a;
	opacity: 1;
}

.new-payment-info__control:focus,
.new-payment-info /deep/ .form-control:focus,
.new-payment-info /deep/ .custom-select:focus {
	border-color: #66acc6;
	box-shadow: 0 0 0 .2rem rgba(102, 172, 198, .15);
}

.new-payment-info__control--large {
	width: 95.2%;
	max-width: 95.2%;
	height: 73px;
	margin-bottom: 16px;
	padding: 0 21px;
}

.new-payment-info__compact-row {
	display: flex;
	align-items: flex-end;
	gap: 2.4%;
	width: 95.2%;
}

.new-payment-info__compact-row label {
	display: flex;
	flex: 1 1 32%;
	flex-direction: column;
	gap: 9px;
	min-width: 0;
	margin: 0;
	color: #5a5a5a;
	font-size: 15px;
	font-weight: 400;
	line-height: 18px;
}

.new-payment-info__control--small,
.new-payment-info__control--select {
	min-width: 0;
	height: 46px;
	padding: 0 10px;
	font-size: 15px;
}

.new-payment-info__expiration {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 5px;
	min-width: 0;
	width: 100%;
}

.new-payment-info__receipt {
	display: flex;
	flex-direction: column;
	gap: 15px;
	max-width: 415px;
}

.new-payment-info__receipt .new-payment-info__control,
.new-payment-info__receipt /deep/ .form-control {
	height: 47px;
	padding: 0 21px;
}

.new-payment-info__receipt-row {
	display: flex;
	flex-wrap: wrap;
	gap: 11px;
}

.new-payment-info__receipt-row .new-payment-info__control {
	flex: 1 1 202px;
}

.new-payment-info__inscription {
	display: flex;
	align-items: center;
	gap: 12px 18px;
	margin-top: 8px;
	font-size: 15px;
	font-weight: 400;
	line-height: 18px;
}

.new-payment-info__inscription span {
	margin-right: 2px;
}

.new-payment-info__inscription label {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin: 0;
	color: #5a5a5a;
	font-size: 15px;
	line-height: 18px;
	cursor: pointer;
}

.new-payment-info__inscription .new-payment-info__control {
	flex: 0 0 202px;
	height: 47px;
	padding: 0 18px;
	font-size: 16px;
}

.new-payment-info__actions {
	display: flex;
	justify-content: flex-end;
	gap: 34px;
	margin-top: 56px;
}

.new-payment-info__submit {
	box-sizing: border-box;
	width: 200px;
	height: 40px;
	margin: 0;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

@media (max-width: 900px) {
	.new-payment-info {
		margin-top: 56px;
	}

	.new-payment-info__grid {
		display: block;
		gap: 42px;
	}

	.new-payment-info__section {
		max-width: none;
	}

	.new-payment-info__section + .new-payment-info__section {
		margin-top: 42px;
	}

	.new-payment-info__section h3 {
		margin-bottom: 32px;
	}

	.new-payment-info__compact-row,
	.new-payment-info__receipt-row {
		display: flex;
		flex-direction: column;
	}

	.new-payment-info__control--large,
	.new-payment-info__compact-row {
		width: 100%;
		max-width: 100%;
	}

	.new-payment-info__compact-row label {
		flex: none;
		width: 100%;
	}

	.new-payment-info__receipt-row .new-payment-info__control {
		flex: none;
	}

	.new-payment-info__receipt {
		max-width: none;
	}

	.new-payment-info__inscription {
		display: grid;
		grid-template-columns: auto auto auto;
	}

	.new-payment-info__inscription .new-payment-info__control {
		grid-column: 1 / -1;
		width: 100%;
	}

	.new-payment-info__actions {
		justify-content: flex-start;
		margin-top: 36px;
	}

	.new-payment-info__submit {
		width: 100%;
		max-width: 200px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/shared/CreditCardInfo.vue